import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, Trash2, X } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import {
  useNotificationsInbox,
  isSafeInternalLink,
  NOTIF_TYPE_COLOR,
} from '../hooks/useNotificationsInbox';

const NotificationBell = ({ isAuthenticated }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const panelRef = useRef(null);

  const {
    notifications, unread, load, markAllRead, markRead, deleteOne, setOpenRef,
  } = useNotificationsInbox({ enabled: !!isAuthenticated, pollMs: 30000, trackUnread: true });

  useEffect(() => { setOpenRef(open); }, [open, setOpenRef]);

  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleMarkAllRead = async () => {
    setActionLoading(true);
    try { await markAllRead(); }
    catch { toast({ title: 'Không thể đánh dấu đã đọc', variant: 'destructive' }); }
    finally { setActionLoading(false); }
  };

  const handleClick = async (n) => {
    if (!n.read) {
      try { await markRead(n.id); } catch {}
    }
    setOpen(false);
    if (isSafeInternalLink(n.link)) navigate(n.link);
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    try { await deleteOne(id); }
    catch { toast({ title: 'Không thể xóa thông báo', variant: 'destructive' }); }
  };

  if (!isAuthenticated) return null;

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => { setOpen(v => !v); if (!open) load().catch(() => {}); }}
        className="relative p-2 rounded-full hover:bg-gray-100 transition-colors"
        aria-label="Thông báo"
      >
        <Bell className="h-5 w-5 text-gray-600" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-xs rounded-full flex items-center justify-center px-0.5 font-bold leading-none">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-2xl border z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
            <h3 className="font-semibold text-sm text-gray-800">Thông báo</h3>
            <div className="flex items-center gap-2">
              {unread > 0 && (
                <button onClick={handleMarkAllRead} disabled={actionLoading}
                  className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed">
                  <CheckCheck className="h-3.5 w-3.5" /> Đọc tất cả
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto divide-y">
            {notifications.length === 0 ? (
              <div className="py-10 text-center text-gray-400 text-sm">Chưa có thông báo nào</div>
            ) : notifications.map(n => (
              <div key={n.id}
                onClick={() => handleClick(n)}
                className={`flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors
                  ${!n.read ? 'bg-blue-50/50' : ''}`}>
                <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${NOTIF_TYPE_COLOR[n.type] || 'bg-blue-400'}`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${!n.read ? 'text-gray-900' : 'text-gray-600'}`}>{n.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{n.body}</p>
                  <p className="text-xs text-gray-400 mt-1">{new Date(n.created_at).toLocaleString('vi-VN')}</p>
                </div>
                <button onClick={(e) => handleDelete(e, n.id)}
                  className="text-gray-300 hover:text-red-400 flex-shrink-0 mt-0.5 p-0.5 rounded">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>

          <div className="border-t px-4 py-2 bg-gray-50">
            <button onClick={() => { navigate('/notifications'); setOpen(false); }}
              className="text-xs text-blue-600 hover:text-blue-800 w-full text-center">
              Xem tất cả thông báo →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
